import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { shallowEqual, useStore } from 'react-redux';
import { toast } from 'react-toastify';
import {
  useTasksControllerGenerateOutputMutation,
  useTasksControllerGetTaskOutFileUrlQuery,
} from 'src/app/services/api.generated';
import { useLazyTasksControllerGetTaskOutputStateQuery } from 'src/app/services/api';
import type { RootStateType } from 'src/app/store';
import {
  useEditPageTask,
  useRoutedTaskId,
} from 'src/components/hooks/useEditPageTask';
import { useEditorMetrics } from 'src/components/hooks/useEditorMetrics';
import { MANUAL_CHECK_KEYS, manualCheckPagesOf } from 'src/utils/manualCheck';
import {
  isGeneratingOutputState,
  OUTPUT_STATE_STILL_GENERATING_AFTER_MS,
  startOutputStatePolling,
} from 'src/utils/taskProgressStreamUtils';

/** Loaded item ids sent with generate-output so the server can check them. */
export const GENERATE_OUTPUT_ITEM_ID_SAMPLE = 50;

export const collectLoadedItemIds = (
  state: RootStateType,
  taskId: string,
  limit = GENERATE_OUTPUT_ITEM_ID_SAMPLE
): string[] => {
  const { routeTaskId, task, pages } = state.editPage;
  if (routeTaskId !== taskId || task?.id !== taskId) return [];
  const ids: string[] = [];
  for (const page of pages) {
    if (!page) continue;
    for (const item of page) {
      if (item.id) ids.push(item.id);
      if (ids.length >= limit) return ids;
    }
  }
  return ids;
};

export const useTaskOutputActions = () => {
  const { t } = useTranslation();
  const store = useStore<RootStateType>();
  const routeTaskId = useRoutedTaskId();
  // Only the routed document: null while the slice still holds another one.
  const { id, outputState, name, numberOfPages, manualCheckPages } =
    useEditPageTask(
      (task) => ({
        id: task?.id,
        outputState: task?.outputState,
        name: task?.name,
        numberOfPages: task?.numberOfPages,
        manualCheckPages: manualCheckPagesOf(task ?? {}),
      }),
      shallowEqual
    );

  const trackEditorEvent = useEditorMetrics();
  const [generateOutput, { isLoading: isGenerateMutationLoading }] =
    useTasksControllerGenerateOutputMutation();

  const [getTaskOutputState] = useLazyTasksControllerGetTaskOutputStateQuery();
  const [isStillGenerating, setIsStillGenerating] = useState(false);

  const isGenerating = useMemo(
    () => isGeneratingOutputState(outputState),
    [outputState]
  );

  // currentData: never the previous document's download URL.
  const { currentData: outFileUrl } = useTasksControllerGetTaskOutFileUrlQuery(
    { taskId: id! },
    { skip: !id || outputState !== 'GENERATED_OUTPUT' }
  );

  const canDownload = outputState === 'GENERATED_OUTPUT';

  useEffect(() => {
    if (!isGenerating) {
      setIsStillGenerating(false);
    }
  }, [isGenerating]);

  useEffect(() => {
    if (!isGenerating || !id) return;

    const startedAt = Date.now();
    setIsStillGenerating(false);

    const stop = startOutputStatePolling(async () => {
      const elapsed = Date.now() - startedAt;
      if (elapsed >= OUTPUT_STATE_STILL_GENERATING_AFTER_MS) {
        setIsStillGenerating(true);
      }

      const res = await getTaskOutputState({ id }, false).unwrap();
      if (isGeneratingOutputState(res.outputState)) {
        return true;
      }

      // Worker failure currently resets to NOT_GENERATED (ERROR is reserved).
      if (res.outputState === 'ERROR' || res.outputState === 'NOT_GENERATED') {
        toast.error(t('generateOutputFailed'));
      }
      // Edited while it ran: the finished file misses the latest change.
      if (res.outputState === 'OUTDATED_OUTPUT') {
        toast.warning(t('exportOutdated'), { toastId: `export-outdated-${id}` });
      }
      return false;
    });

    return stop;
  }, [getTaskOutputState, id, isGenerating, t]);

  const generateDocument = () => {
    if (!id || id !== routeTaskId) return;
    setIsStillGenerating(false);
    // Before the request, once the click is registered.
    trackEditorEvent('editor_redaction_applied', numberOfPages);
    const itemIds = collectLoadedItemIds(store.getState(), id);
    void generateOutput({
      generateOutputDto: itemIds.length ? { id, itemIds } : { id },
    }).unwrap();
    // Warn only: the export is not blocked.
    if (manualCheckPages > 0) {
      toast.warning(t(MANUAL_CHECK_KEYS.exportWarning, { n: manualCheckPages }));
    }
  };

  const downloadDocument = () => {
    if (!outFileUrl || !id || id !== routeTaskId) return;
    trackEditorEvent('editor_export_clicked', numberOfPages);
    const anchor = document.createElement('a');
    document.body.appendChild(anchor);
    anchor.href = outFileUrl.url;
    anchor.download =
      name?.split('.').slice(0, -1).join('.') + `(${t('redacted')})` ||
      'outputFile';
    anchor.click();
    document.body.removeChild(anchor);
  };

  return {
    id,
    name,
    numberOfPages,
    outputState,
    isGenerating,
    isStillGenerating,
    isGenerateMutationLoading,
    canDownload,
    generateDocument,
    downloadDocument,
  };
};
